import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
    ArrowLeft, Mail, Phone, Globe, MapPin, Calendar as CalendarIcon, 
    Clock, Ticket, Share2, ExternalLink, ChevronLeft, ChevronRight, Search, List, Grid 
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths } from 'date-fns';
import api from '../api';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

export default function PublicPartnerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [partner, setPartner] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
    const [calendarDate, setCalendarDate] = useState(new Date());

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const partnersData = await api.getPartners();
            const foundPartner = partnersData.find(p => p.id === id);
            setPartner(foundPartner);

            if (foundPartner) {
                const eventsData = await api.getEvents();
                const associated = eventsData.filter(e => 
                    e.organizer && e.organizer.toLowerCase() === foundPartner.name.toLowerCase()
                );
                setEvents(associated);
            }
        } catch (error) {
            console.error('Error loading partner data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrevMonth = () => setCalendarDate(subMonths(calendarDate, 1));
    const handleNextMonth = () => setCalendarDate(addMonths(calendarDate, 1));

    if (loading) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '40px 0' }}>
                    <div className="skeleton" style={{ height: '300px', borderRadius: '24px', marginBottom: '40px' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '32px' }}>
                        <div className="skeleton" style={{ height: '500px', borderRadius: '24px' }} />
                        <div className="skeleton" style={{ height: '400px', borderRadius: '24px' }} />
                    </div>
                </div>
            </main>
        );
    }

    if (!partner) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h2>Partner not found</h2>
                    <Link to="/partners" className="btn btn-primary" style={{ marginTop: '24px' }}>
                        Back to Partners
                    </Link>
                </div>
            </main>
        );
    }

    // Calendar logic
    const monthStart = startOfMonth(calendarDate);
    const monthEnd = endOfMonth(calendarDate);
    const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

    return (
        <main className="main" style={{ background: '#FAF8F5' }}>
            <SEO 
                title={partner.name}
                description={partner.description || `Learn more about ${partner.name}, our arts partner in Singapore.`}
                keywords={`${partner.name}, arts partner, Singapore Indian arts, ${partner.category}`}
            />
            
            <div className="container" style={{ padding: '40px 24px' }}>
                {/* Header/Banner Section */}
                <div style={{ position: 'relative', marginBottom: '80px' }}>
                    {/* Banner */}
                    <div style={{ 
                        height: '280px', 
                        borderRadius: '24px', 
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        position: 'relative'
                    }}>
                        <img 
                            src={partner.banner_url || 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&q=80'} 
                            alt="Banner" 
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                opacity: 0.6
                            }} 
                        />
                        {/* Back Button */}
                        <button 
                            onClick={() => navigate(-1)}
                            style={{
                                position: 'absolute',
                                top: '24px',
                                left: '24px',
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(12px)',
                                width: '40px', height: '40px',
                                borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', border: '1px solid rgba(255,255,255,0.3)'
                            }}
                        >
                            <ArrowLeft size={20} />
                        </button>
                    </div>

                    {/* Profile Overlay */}
                    <div style={{ 
                        position: 'absolute', 
                        bottom: '-40px', 
                        left: '40px',
                        display: 'flex',
                        alignItems: 'flex-end',
                        width: 'calc(100% - 80px)',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px' }}>
                            <div style={{ 
                                width: '120px', 
                                height: '120px', 
                                borderRadius: '24px', 
                                background: 'linear-gradient(135deg, #FFDEE9 0%, #B5FFFC 100%)',
                                padding: '8px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                background: '#fff'
                            }}>
                                <div style={{ 
                                    width: '100%', height: '100%', 
                                    borderRadius: '16px',
                                    background: 'var(--primary-gradient)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '48px', fontWeight: '800', color: '#000'
                                }}>
                                    {partner.logo_url ? <img src={partner.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }} /> : partner.name.charAt(0)}
                                </div>
                            </div>
                        </div>

                        {/* Top Action Buttons */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                            <button className="btn btn-secondary" style={{ padding: '8px 20px', borderRadius: '12px', background: '#fff' }}>
                                <Share2 size={16} /> Share
                            </button>
                            {isAdmin && (
                                <Link to={`/admin/partners/${partner.id}`} className="btn" style={{ 
                                    background: '#FFF3EE', 
                                    color: '#E06D32', 
                                    border: '1px solid #FFD9C7',
                                    padding: '8px 20px',
                                    borderRadius: '12px',
                                    fontWeight: '600'
                                }}>
                                    Manage <ExternalLink size={16} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* Title and Meta */}
                <div style={{ marginBottom: '40px', paddingLeft: '40px' }}>
                    <h1 style={{ fontSize: '42px', fontWeight: '800', color: '#2D1E1E', marginBottom: '8px' }}>{partner.name}</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#908686', fontSize: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={16} /> Times in GMT+5:30 — 1:56 PM
                        </div>
                    </div>
                </div>

                <div style={{ height: '1px', background: 'rgba(0,0,0,0.05)', marginBottom: '40px' }} />

                {/* Main Content Layout */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '48px' }}>
                    
                    {/* Left Side: Events and Info */}
                    <div>
                        {/* Section Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                            <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#2D1E1E' }}>Events</h2>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ display: 'flex', background: '#F0EDED', borderRadius: '10px', padding: '4px' }}>
                                    <button 
                                        onClick={() => setViewMode('grid')}
                                        style={{ padding: '6px', borderRadius: '6px', background: viewMode === 'grid' ? '#fff' : 'transparent', boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none' }}>
                                        <Grid size={18} color={viewMode === 'grid' ? '#000' : '#908686'} />
                                    </button>
                                    <button 
                                        onClick={() => setViewMode('list')}
                                        style={{ padding: '6px', borderRadius: '6px', background: viewMode === 'list' ? '#fff' : 'transparent', boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none' }}>
                                        <List size={18} color={viewMode === 'list' ? '#000' : '#908686'} />
                                    </button>
                                </div>
                                <button style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#F0EDED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Search size={18} color="#908686" />
                                </button>
                            </div>
                        </div>

                        {/* Events List/Grid */}
                        {events.length === 0 ? (
                            <div style={{ 
                                background: '#FFFDFB', 
                                border: '1px dashed #E5E1DA', 
                                borderRadius: '32px', 
                                padding: '80px 40px', 
                                textAlign: 'center' 
                            }}>
                                <div style={{ position: 'relative', width: '140px', height: '120px', margin: '0 auto 32px' }}>
                                    <div style={{ 
                                        width: '100px', height: '100px', 
                                        background: '#F8F7F3', borderRadius: '20px',
                                        margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <div style={{ opacity: 0.2 }}>
                                            <CalendarIcon size={48} />
                                        </div>
                                    </div>
                                    <div style={{ 
                                        position: 'absolute', top: -10, right: 0,
                                        width: '40px', height: '40px', 
                                        background: '#F8F7F3', borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '20px', fontWeight: '800', color: '#908686'
                                    }}>0</div>
                                </div>
                                <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#908686', marginBottom: '8px' }}>No Past Events</h3>
                                <p style={{ color: '#BAB3AF' }}>The calendar is empty. Why not add a new event now?</p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr', gap: '24px' }}>
                                {events.map((event) => (
                                    <Link key={event.id} to={`/events/${event.id}`} style={{ textDecoration: 'none' }}>
                                        <div className="card" style={{ 
                                            display: 'flex', 
                                            flexDirection: viewMode === 'grid' ? 'column' : 'row',
                                            padding: '0', 
                                            overflow: 'hidden',
                                            borderRadius: '24px',
                                            height: viewMode === 'grid' ? '100%' : 'auto'
                                        }}>
                                            <div style={{ 
                                                width: viewMode === 'grid' ? '100%' : '180px', 
                                                height: viewMode === 'grid' ? '180px' : 'auto',
                                                flexShrink: 0
                                            }}>
                                                <img 
                                                    src={event.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600'} 
                                                    alt={event.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div style={{ padding: '24px', flex: 1 }}>
                                                <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#2D1E1E', marginBottom: '12px' }}>{event.title}</h4>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#908686', fontSize: '14px' }}>
                                                        <CalendarIcon size={14} /> {format(new Date(event.date), 'MMM d, yyyy')}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#908686', fontSize: '14px' }}>
                                                        <MapPin size={14} /> {event.location}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* About Section */}
                        <div style={{ marginTop: '64px' }}>
                            <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#2D1E1E', marginBottom: '24px' }}>About {partner.name}</h3>
                            <div style={{ background: '#fff', borderRadius: '32px', padding: '40px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                                <p style={{ color: '#4D4141', lineHeight: 1.8, fontSize: '16px', marginBottom: '32px' }}>
                                    {partner.description}
                                </p>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px' }}>
                                    {partner.artforms && partner.artforms.length > 0 && (
                                        <div>
                                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#908686', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>Art Forms</h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {partner.artforms.map((art, idx) => (
                                                    <span key={idx} style={{ background: '#F8F7F3', padding: '8px 16px', borderRadius: '12px', fontSize: '14px', fontWeight: '600' }}>{art}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {partner.services && partner.services.length > 0 && (
                                        <div>
                                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#908686', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>Services</h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {partner.services.map((s, idx) => (
                                                    <span key={idx} style={{ border: '1px solid #E5E1DA', padding: '8px 16px', borderRadius: '12px', fontSize: '14px', fontWeight: '600' }}>{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Sidebar Calendar and Contact */}
                    <div>
                        {/* Compact Calendar */}
                        <div style={{ background: '#fff', borderRadius: '24px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', marginBottom: '32px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: '800' }}>{format(calendarDate, 'MMMM')}</h3>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handlePrevMonth} style={{ background: '#F8F7F3', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button onClick={handleNextMonth} style={{ background: '#F8F7F3', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', textAlign: 'center', marginBottom: '16px' }}>
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                                    <div key={day} style={{ fontSize: '12px', fontWeight: '700', color: '#BAB3AF' }}>{day}</div>
                                ))}
                                {calendarDays.map(day => {
                                    const hasEvent = events.some(e => format(new Date(e.date), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));
                                    return (
                                        <div key={day.toString()} style={{ 
                                            fontSize: '14px', 
                                            fontWeight: '600', 
                                            padding: '8px 0',
                                            color: isToday(day) ? 'var(--primary)' : '#4D4141',
                                            position: 'relative'
                                        }}>
                                            {format(day, 'd')}
                                            {hasEvent && <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ display: 'flex', background: '#F8F7F3', borderRadius: '12px', padding: '4px' }}>
                                <button style={{ flex: 1, padding: '8px', borderRadius: '8px', background: '#fff', fontSize: '13px', fontWeight: '700', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>Upcoming</button>
                                <button style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', color: '#908686' }}>Past</button>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div style={{ background: '#fff', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '24px' }}>Contact Details</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {partner.website && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(222, 65, 176, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Globe size={18} color="var(--primary)" />
                                        </div>
                                        <a href={`https://${partner.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)', fontWeight: '600' }}>Website</a>
                                    </div>
                                )}
                                {partner.email && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(5, 150, 105, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Mail size={18} color="var(--success)" />
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            <div style={{ color: '#908686', fontSize: '12px' }}>Email</div>
                                            <a href={`mailto:${partner.email}`} style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{partner.email}</a>
                                        </div>
                                    </div>
                                )}
                                {partner.phone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(217, 119, 6, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Phone size={18} color="var(--accent)" />
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            <div style={{ color: '#908686', fontSize: '12px' }}>Phone</div>
                                            <span style={{ fontWeight: '600' }}>{partner.phone}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
